/**
 * View query operations — shared between client and server.
 *
 * These are the SINGLE SOURCE OF TRUTH for what tasks appear in each view.
 * Both the web app (via useLiveQuery) and the server API use these functions
 * to guarantee identical filtering and sorting.
 *
 * Uses getDb() from runtime.ts — caller must have configured the context
 * via setOpsContext (client) or setContextResolver (server).
 */

import { cmp } from "dexie"
import { getDb, getOpsTimeZoneOrLookup } from "./runtime.js"
import dayjs from "dayjs"
import {
  ObjectType,
  ObjectStatus,
  StartAtType,
  StartAtSubType,
  type DbObject,
  type DbStartAtIndex,
} from "./db-types.js"
import { ok, fail, type OpResult } from "./op-types.js"
import { getChecklistView, type ChecklistView, type ChecklistViewPage } from "./checklistOps.js"
import { parseDbDate } from "./utils.js"
import { addDaysToDay, endOfDayMs, localDayOf, localHourOf } from "./dayTime.js"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Lowercase + strip diacritics (è→e, å→a, ö→o, etc.) for fuzzy matching */
function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ViewItem {
  task: DbObject
  sortIndex: number
  startAtData?: DbStartAtIndex
}

export interface ViewData {
  items: ViewItem[]
  laterItems: ViewItem[]
}

// ---------------------------------------------------------------------------
// Evening detection
// ---------------------------------------------------------------------------

const DEFAULT_EVENING_HOUR = 18

/**
 * Read the user's configured evening hour from `db.setting`.
 * Falls back to 18 (6 PM) if no setting exists.
 */
export async function getEveningHour(): Promise<number> {
  try {
    const row = await getDb().setting.get("#evening-time")
    if (row?.value) {
      const parsed = parseInt(row.value, 10)
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 23) return parsed
    }
  } catch {
    // DB not ready — fall back
  }
  return DEFAULT_EVENING_HOUR
}

/**
 * Determine if a task should appear in the "This Evening" view right now.
 *
 * - Tomorrow's evening task, current time ≥ eveningHour → true (pull forward)
 * - Today's evening task, current time < eveningHour → true (still in section)
 * - All other cases → false
 *
 * Days and the hour are read in `timeZone` (an IANA name) when one is given - the server passes the
 * user's zone, its own clock being UTC - and in the runtime's local zone otherwise (the app).
 */
export function isEvening(
  entry: DbStartAtIndex,
  eveningHour: number = DEFAULT_EVENING_HOUR,
  timeZone?: string,
): boolean {
  if (entry.subType !== StartAtSubType.ThisEvening) return false

  const taskMs = Number(entry.startAt)
  if (!taskMs) return false

  if (timeZone) {
    const nowMs = Date.now()
    const today = localDayOf(nowMs, timeZone)
    const taskDay = localDayOf(taskMs, timeZone)
    const hour = localHourOf(nowMs, timeZone)
    if (taskDay === addDaysToDay(today, 1) && hour >= eveningHour) return true
    if (taskDay === today && hour < eveningHour) return true
    return false
  }

  const taskDate = dayjs(taskMs)
  const now = dayjs()

  // Tomorrow's evening task, pulled forward because it's past evening hour
  if (now.add(1, "day").isSame(taskDate, "day") && now.hour() >= eveningHour) {
    return true
  }

  // Today's evening task, before evening hour — still in evening section
  if (now.isSame(taskDate, "day") && now.hour() < eveningHour) {
    return true
  }

  return false
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function hasValidStartAt(entry: DbStartAtIndex): boolean {
  return !!entry.startAt && Number(entry.startAt) > 0
}

/** The end of today in `timeZone` (the runtime's local zone without one): what "today" means for a view. */
function endOfToday(timeZone: string | undefined): number {
  return endOfDayMs(Date.now(), timeZone)
}

function endOfTomorrow(timeZone: string | undefined): number {
  return endOfDayMs(Date.now(), timeZone, 1)
}

const VALID_VIEW_TYPES = new Set([
  ObjectType.Task,
  ObjectType.Project,
  ObjectType.List,
])

function isValidViewItem(t: DbObject): boolean {
  return (
    t.status === ObjectStatus.Unfinished &&
    t.isDeleted === 0 &&
    VALID_VIEW_TYPES.has(t.type) &&
    !t.isRecurringTemplate
  )
}

/**
 * Build a whitelist of active (unfinished, non-deleted) container IDs
 * referenced by the given tasks' parentPaths.
 */
async function getActiveContainerIds(
  tasks: (DbObject | undefined)[],
): Promise<Set<string>> {
  const db = getDb()
  const containerIds = new Set<string>()
  for (const t of tasks) {
    if (!t) continue
    const segs = t.parentPath.split("/")
    if (segs.length === 2 && !segs[1]!.startsWith("#")) {
      containerIds.add(segs[1]!)
    }
  }
  const active = new Set<string>()
  if (containerIds.size > 0) {
    const containers = await db.totodo.bulkGet(Array.from(containerIds))
    for (const c of containers) {
      if (c && c.status === ObjectStatus.Unfinished && c.isDeleted === 0) {
        active.add(c.id!)
      }
    }
  }
  return active
}

// ---------------------------------------------------------------------------
// View queries
// ---------------------------------------------------------------------------

// Query style for the view queries below: read a plain index range with
// .toArray() and filter in JS. Dexie serves a plain range with ONE getAll();
// adding .filter(), anyOf() or reverse() makes it iterate a cursor with a
// round trip to the main thread per row. Under startup load that stretched
// Today's 650-entry read to ~10s, and IndexedDB does not start a readwrite
// transaction on the same store until earlier reads finish — the user's new
// task waited behind it. Same rows, same order; only the transport differs.

/**
 * Today view: startAtType=Default, startAt ≤ today, !isEvening.
 *
 * Only shows tasks whose parent is either:
 * - "root/#anyday" (root-level today tasks)
 * - "root/{containerId}" where the container is unfinished & not deleted
 *
 * Tasks in completed/deleted containers or in inbox are excluded.
 */
export async function queryTodayTasks(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const zone = getOpsTimeZoneOrLookup()
    const timeZone = zone instanceof Promise ? await zone : zone
    const todayEnd = endOfToday(timeZone)
    const eveningHour = await getEveningHour()

    const entries = (
      await db.startAtIndex.where("type").equals(StartAtType.Default).toArray()
    ).filter(
      (e) =>
        hasValidStartAt(e) &&
        Number(e.startAt) <= todayEnd &&
        !isEvening(e, eveningHour, timeZone),
    )

    const taskIds = entries.map((e) => e.id!.slice(1))
    const tasks = await db.totodo.bulkGet(taskIds)
    const entryMap = new Map(entries.map((e) => [e.id!.slice(1), e]))
    const activeContainers = await getActiveContainerIds(tasks)

    const items: ViewItem[] = []
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i]
      if (!t || !isValidViewItem(t)) continue

      if (t.type === ObjectType.Task) {
        const segs = t.parentPath.split("/")
        if (segs.length === 2 && !segs[1]!.startsWith("#")) {
          if (!activeContainers.has(segs[1]!)) continue
        } else if (t.parentPath !== "root/#anyday") {
          continue
        }
      }

      const entry = entryMap.get(t.id!)
      items.push({
        task: t,
        sortIndex: entry?.index ?? t.index,
        startAtData: entry,
      })
    }

    items.sort((a, b) => a.sortIndex - b.sortIndex)
    return ok({ items, laterItems: [] })
  } catch (e) {
    return fail(`queryTodayTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Evening view: startAtType=Default, subType=ThisEvening, isEvening()=true.
 */
export async function queryEveningTasks(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const zone = getOpsTimeZoneOrLookup()
    const timeZone = zone instanceof Promise ? await zone : zone
    const tomorrowEnd = endOfTomorrow(timeZone)
    const eveningHour = await getEveningHour()

    const entries = (
      await db.startAtIndex.where("type").equals(StartAtType.Default).toArray()
    ).filter(
      (e) =>
        e.subType === StartAtSubType.ThisEvening &&
        hasValidStartAt(e) &&
        Number(e.startAt) <= tomorrowEnd &&
        isEvening(e, eveningHour, timeZone),
    )

    const taskIds = entries.map((e) => e.id!.slice(1))
    const tasks = await db.totodo.bulkGet(taskIds)
    const entryMap = new Map(entries.map((e) => [e.id!.slice(1), e]))

    const items: ViewItem[] = []
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i]
      if (!t || !isValidViewItem(t)) continue

      const entry = entryMap.get(t.id!)
      items.push({
        task: t,
        sortIndex: entry?.index ?? t.index,
        startAtData: entry,
      })
    }

    items.sort((a, b) => a.sortIndex - b.sortIndex)
    return ok({ items, laterItems: [] })
  } catch (e) {
    return fail(`queryEveningTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Upcoming view: startAtType=Default, subType=Default, startAt > today.
 * Sorted by startAt date ASC.
 */
export async function queryUpcomingTasks(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const zone = getOpsTimeZoneOrLookup()
    const timeZone = zone instanceof Promise ? await zone : zone
    const todayEnd = endOfToday(timeZone)

    const entries = (
      await db.startAtIndex.where("type").equals(StartAtType.Default).toArray()
    ).filter(
      (e) =>
        hasValidStartAt(e) &&
        Number(e.startAt) > todayEnd &&
        e.subType === StartAtSubType.Default,
    )

    const taskIds = entries.map((e) => e.id!.slice(1))
    const tasks = await db.totodo.bulkGet(taskIds)
    const entryMap = new Map(entries.map((e) => [e.id!.slice(1), e]))

    const items: ViewItem[] = []
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i]
      if (!t || !isValidViewItem(t)) continue

      const entry = entryMap.get(t.id!)
      items.push({
        task: t,
        sortIndex: entry?.index ?? t.index,
        startAtData: entry,
      })
    }

    items.sort((a, b) => {
      const aDate = Number(a.startAtData?.startAt ?? "0")
      const bDate = Number(b.startAtData?.startAt ?? "0")
      return aDate - bDate || a.sortIndex - b.sortIndex
    })

    // Recurring templates — shown in "Repeating To-Dos" section.
    // Only NO_PILE templates appear here (like Things 3's "Waiting" list).
    // ALLOW templates already have instances visible in the calendar.
    const templates = (
      await db.totodo.where("isRecurringTemplate").equals(1).toArray()
    ).filter((t) => t.isDeleted === 0 && (t.type === ObjectType.Task || t.type === ObjectType.Project || t.type === ObjectType.List))

    // Filter out ALLOW-pile templates (their instances show in the calendar directly)
    const templateIds = templates.map((t) => t.id!)
    const recurrences = await db.recurrence.bulkGet(templateIds)
    const allowSet = new Set(
      recurrences
        .filter((r) => r?.pilePolicy === "ALLOW")
        .map((r) => r!.id),
    )
    const filteredTemplates = templates.filter((t) => !allowSet.has(t.id!))

    const laterItems: ViewItem[] = filteredTemplates.map((t) => ({
      task: t,
      sortIndex: t.index,
    }))
    laterItems.sort((a, b) => a.task.title.localeCompare(b.task.title))

    return ok({ items, laterItems })
  } catch (e) {
    return fail(`queryUpcomingTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Someday view: startAtType=Someday, startAt ≤ today, subType=Default.
 * Tasks only (no containers).
 */
export async function querySomedayTasks(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const zone = getOpsTimeZoneOrLookup()
    const timeZone = zone instanceof Promise ? await zone : zone
    const todayEnd = endOfToday(timeZone)

    const entries = (
      await db.startAtIndex.where("type").equals(StartAtType.Someday).toArray()
    ).filter(
      (e) =>
        hasValidStartAt(e) &&
        Number(e.startAt) <= todayEnd &&
        e.subType === StartAtSubType.Default,
    )

    const taskIds = entries.map((e) => e.id!.slice(1))
    const tasks = await db.totodo.bulkGet(taskIds)
    const entryMap = new Map(entries.map((e) => [e.id!.slice(1), e]))

    const items: ViewItem[] = []
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i]
      if (!t) continue
      if (t.status !== ObjectStatus.Unfinished) continue
      if (t.isDeleted !== 0) continue
      if (t.type !== ObjectType.Task) continue

      const entry = entryMap.get(t.id!)
      items.push({
        task: t,
        sortIndex: entry?.index ?? t.index,
        startAtData: entry,
      })
    }

    items.sort((a, b) => a.sortIndex - b.sortIndex)
    return ok({ items, laterItems: [] })
  } catch (e) {
    return fail(`querySomedayTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Anyday view: all unfinished Task/Section, EXCEPT Someday bucket.
 * Matches v1's "Anytime" — overlaps with Today/Upcoming/Evening by design.
 */
export async function queryAnydayTasks(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const zone = getOpsTimeZoneOrLookup()
    const timeZone = zone instanceof Promise ? await zone : zone
    const todayEnd = endOfToday(timeZone)

    // Keys in ascending order, so the concatenation matches anyOf()'s order.
    const allTasks = (
      await Promise.all(
        [
          [ObjectType.Task, 0, ObjectStatus.Unfinished],
          [ObjectType.Section, 0, ObjectStatus.Unfinished],
        ].map((key) =>
          db.totodo.where("[type+isDeleted+status]").equals(key).toArray(),
        ),
      )
    ).flat()

    const startAtIds = allTasks.filter((t) => t.id).map((t) => `#${t.id}`)
    const startAtEntries = await db.startAtIndex.bulkGet(startAtIds)
    const entryMap = new Map<string, DbStartAtIndex>()
    for (let i = 0; i < startAtIds.length; i++) {
      const sid = startAtIds[i]
      if (startAtEntries[i] && sid) {
        entryMap.set(sid.slice(1), startAtEntries[i]!)
      }
    }

    const items: ViewItem[] = []
    for (const t of allTasks) {
      if (!t.id) continue
      if (t.status === ObjectStatus.Hidden) continue
      if (t.parentPath === "root/#quick-add") continue

      const entry = entryMap.get(t.id)
      if (entry) {
        const isSomedayBucket =
          hasValidStartAt(entry) &&
          Number(entry.startAt) <= todayEnd &&
          entry.type === StartAtType.Someday &&
          entry.subType === StartAtSubType.Default
        if (isSomedayBucket) continue
      }

      items.push({
        task: t,
        sortIndex: t.index,
        startAtData: entry,
      })
    }

    items.sort((a, b) => a.sortIndex - b.sortIndex)
    return ok({ items, laterItems: [] })
  } catch (e) {
    return fail(`queryAnydayTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Inbox: parentPath = "root/#inbox", unfinished Task/Section.
 */
export async function queryInbox(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const tasks = (
      await db.totodo
        .where("[parentPath+isDeleted]")
        .equals(["root/#inbox", 0])
        .toArray()
    )
      .filter(
        (t) =>
          (t.type === ObjectType.Task || t.type === ObjectType.Section) &&
          t.status === ObjectStatus.Unfinished,
      )
      // Dexie's sortBy("index") sorts with cmp() (IndexedDB key order).
      .sort((a, b) => cmp(a.index, b.index))

    const items: ViewItem[] = tasks
      .filter((t) => t.id != null)
      .map((t) => ({ task: t, sortIndex: t.index }))

    return ok({ items, laterItems: [] })
  } catch (e) {
    return fail(`queryInbox: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Logbook: completed + cancelled items, sorted by statusAt DESC.
 */
export async function queryLogbook(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const validTypes = [
      ObjectType.Task,
      ObjectType.Section,
      ObjectType.Project,
      ObjectType.List,
    ]

    // One equals per status, canceled (-1) before completed (1): what anyOf([1, -1]) reads, in the same
    // order, without reading the range between them - which holds every unfinished object, the
    // largest part of the table (on dexie-sqlite a range is read in full, values and all).
    const isLogged = (t: DbObject) =>
      !t.isDeleted && validTypes.includes(t.type) && t.status !== ObjectStatus.Hidden
    const tasks: DbObject[] = []
    for (const status of [ObjectStatus.Canceled, ObjectStatus.Completed]) {
      tasks.push(...(await db.totodo.where("status").equals(status).filter(isLogged).toArray()))
    }

    const items: ViewItem[] = tasks
      .filter((t) => t.id != null)
      .map((t) => ({
        task: t,
        sortIndex: parseDbDate(t.statusAt) ?? 0,
      }))

    items.sort((a, b) => b.sortIndex - a.sortIndex)
    return ok({ items, laterItems: [] })
  } catch (e) {
    return fail(`queryLogbook: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * Trash: deleted items owned by current user, sorted by deletedAt DESC.
 */
export async function queryTrash(): Promise<OpResult<ViewData>> {
  try {
    const db = getDb()
    const currentUser = db.cloud.currentUserId

    // The isDeleted index holds every candidate (it may hold more - dexie-sqlite also matches a
    // boolean true - which the === 1 below drops); only the trash is read, not the table.
    const items = await db.totodo
      .where("isDeleted")
      .equals(1)
      .filter(
        (t) =>
          t.isDeleted === 1 &&
          t.owner === currentUser &&
          (t.type === ObjectType.Task ||
            t.type === ObjectType.Project ||
            t.type === ObjectType.List ||
            t.type === ObjectType.Section),
      )
      .toArray()

    const viewItems: ViewItem[] = items
      .filter((t) => t.id != null)
      .map((t) => ({
        task: t,
        sortIndex: Number(t.deletedAt ?? "0"),
      }))

    viewItems.sort((a, b) => b.sortIndex - a.sortIndex)
    return ok({ items: viewItems, laterItems: [] })
  } catch (e) {
    return fail(`queryTrash: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// Container queries
// ---------------------------------------------------------------------------

export interface ListContainersOpts {
  type?: ObjectType
  parentPath?: string
  includeDeleted?: boolean
}

/**
 * List containers (projects, areas, lists).
 */
export async function listContainers(
  opts?: ListContainersOpts,
): Promise<OpResult<DbObject[]>> {
  try {
    const db = getDb()
    const containerTypes = new Set([
      ObjectType.Project,
      ObjectType.Area,
      ObjectType.List,
    ])

    // One equals per container type instead of the whole table, then back in the table's own
    // (primary key) order, so rows with equal `index` keep the order a full read gave them.
    let items: DbObject[] = []
    for (const type of containerTypes) items.push(...(await db.totodo.where("type").equals(type).toArray()))
    items.sort((a, b) => cmp(a.id ?? "", b.id ?? ""))

    if (opts?.type !== undefined) {
      items = items.filter((i) => i.type === opts.type)
    }
    if (opts?.parentPath !== undefined) {
      items = items.filter((i) => i.parentPath === opts.parentPath)
    }
    if (!opts?.includeDeleted) {
      items = items.filter((i) => i.isDeleted !== 1)
    }

    items.sort((a, b) => a.index - b.index)
    return ok(items)
  } catch (e) {
    return fail(`listContainers: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// List tasks in a container
// ---------------------------------------------------------------------------

export interface ListTasksOpts {
  parentPath: string
  includeDeleted?: boolean
  status?: ObjectStatus
  limit?: number
  offset?: number
}

/**
 * List tasks in a specific container by parentPath.
 */
export async function listTasks(
  opts: ListTasksOpts,
): Promise<OpResult<{ items: DbObject[]; total: number }>> {
  try {
    const db = getDb()
    let items = await db.totodo
      .where("parentPath")
      .equals(opts.parentPath)
      .sortBy("index")

    if (!opts.includeDeleted) {
      items = items.filter((i) => i.isDeleted !== 1)
    }
    if (opts.status !== undefined) {
      items = items.filter((i) => i.status === opts.status)
    }

    const total = items.length

    if (opts.offset) items = items.slice(opts.offset)
    if (opts.limit) items = items.slice(0, opts.limit)

    return ok({ items, total })
  } catch (e) {
    return fail(`listTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/**
 * The userStatus filter of searchTasks as a predicate: `userStatusFilter` matches the normalized value
 * or label (a `system:N` status by its enum name, a legacy number the same way, a custom string
 * directly).
 */
function userStatusMatcher(userStatusFilter: string): (t: DbObject) => boolean {
  const needle = userStatusFilter.toLowerCase().replace(/[\s_-]/g, "")
  const enumKeys: Record<number, string> = {
    1: "idea", 2: "ready", 3: "inprogress", 4: "waiting",
    5: "blocked", 6: "review", 7: "testing", 8: "approved",
    9: "deployed", 10: "done", 11: "onhold", 12: "deferred",
    13: "rejected", 14: "cancelled",
  }
  return (t) => {
    if (t.userStatus == null) return false
    const raw = String(t.userStatus)
    // Match system:N by parsing the enum value name
    if (raw.startsWith("system:")) {
      const num = Number(raw.slice(7))
      // Compare against enum key names (e.g. "InProgress" -> "inprogress")
      const key = enumKeys[num] ?? ""
      return key.includes(needle) || raw === userStatusFilter
    }
    // Legacy numeric values
    if (typeof t.userStatus === "number") {
      const key = enumKeys[t.userStatus] ?? ""
      return key.includes(needle)
    }
    // Custom string status - match directly
    return raw.toLowerCase().includes(needle)
  }
}

export async function searchTasks(
  query: string,
  limit = 50,
  statusFilter: "active" | "completed" | "canceled" | "completed_only" | "all" = "active",
  typeFilter?: "task" | "project" | "list" | "area" | "section",
  tagFilter?: string,
  userStatusFilter?: string,
): Promise<OpResult<DbObject[]>> {
  try {
    const db = getDb()

    // Tag-only search: if tagFilter is provided and query is empty/tag-like, filter by tag
    const normalizedTag = tagFilter?.replace(/^#/, "")

    // Split query into words — filter stop words, ALL remaining must match
    const stopWords = new Set(["och", "eller", "i", "på", "för", "med", "om", "av", "till", "en", "ett", "den", "det", "de", "som", "and", "or", "the", "a", "an", "in", "on", "for", "with", "of", "to"])
    const words = normalize(query).split(/\s+/).filter((w) => w.length > 0 && !stopWords.has(w))
    if (!words.length && !normalizedTag && !userStatusFilter) return ok([])

    const matchesAll = (text: string) => {
      const n = normalize(text)
      return words.every((w) => n.includes(w))
    }

    const hasTag = (tags: string[] | undefined) => {
      if (!normalizedTag || !tags) return !normalizedTag
      // Match both "shopping" and "#shopping" stored forms
      return tags.some((t) => t.replace(/^#/, "") === normalizedTag)
    }

    // Map typeFilter to ObjectType for efficient filtering
    const typeMap: Record<string, number> = {
      task: ObjectType.Task,
      project: ObjectType.Project,
      list: ObjectType.List,
      area: ObjectType.Area,
      section: ObjectType.Section,
    }
    const allowedType = typeFilter ? typeMap[typeFilter] : undefined

    // With a type filter only that type's rows are read (the `type` index; the predicate below still
    // checks `t.type === allowedType`, so a backend that indexes more matches changes nothing).
    const candidates = allowedType === undefined ? db.totodo.toCollection() : db.totodo.where("type").equals(allowedType)

    // The row is visited, scored and dropped: only (id, score, index) of a MATCH is kept, so a search
    // over 20,000+ tasks holds a few thousand small records, never the rows (titles, notes, documents).
    // The page is then read by id.
    const userStatusMatches = userStatusFilter ? userStatusMatcher(userStatusFilter) : undefined
    const nowMs = Date.now()
    const scored: Array<{ id: string; score: number; index: number }> = []
    await candidates.each((t) => {
      if (
        !(
          t.isDeleted !== 1 &&
          t.type !== ObjectType.Root &&
          (allowedType === undefined || t.type === allowedType) &&
          (normalizedTag ? hasTag(t.tags) : true) &&
          (words.length === 0 ||
            (typeof t.title === "string" && matchesAll(t.title)) ||
            (typeof t.note === "string" && matchesAll(t.note)))
        )
      ) {
        return
      }

      // Status filter
      switch (statusFilter) {
        case "all":
          break
        case "completed":
          // Legacy: includes both completed AND canceled
          if (t.status !== ObjectStatus.Completed && t.status !== ObjectStatus.Canceled) return
          break
        case "completed_only":
          if (t.status !== ObjectStatus.Completed) return
          break
        case "canceled":
          if (t.status !== ObjectStatus.Canceled) return
          break
        default:
          // "active" — excludes completed and canceled
          if (t.status === ObjectStatus.Completed || t.status === ObjectStatus.Canceled) return
      }

      // userStatus filter (matches normalized value or label)
      if (userStatusMatches && !userStatusMatches(t)) return

      // Relevance scoring:
      // - Title match vs note-only match (title is stronger signal)
      // - Multiple word hits in title score higher
      // - Active tasks boost (when filter is "all")
      // - Recency boost (recently modified rank higher)
      let score = 0

      // Title match: +10 per word found in title
      if (typeof t.title === "string") {
        const titleNorm = normalize(t.title)
        for (const w of words) {
          if (titleNorm.includes(w)) score += 10
        }
        // Exact full query match in title: bonus
        if (titleNorm.includes(words.join(" "))) score += 20
      }

      // Note-only match: +2 per word
      if (typeof t.note === "string" && score === 0) {
        const noteNorm = normalize(t.note)
        for (const w of words) {
          if (noteNorm.includes(w)) score += 2
        }
      }

      // Status boost (when showing all): active tasks get +5
      if (statusFilter === "all" && t.status !== ObjectStatus.Completed && t.status !== ObjectStatus.Canceled) {
        score += 5
      }

      // Recency: more recent = small boost (0-3 points)
      const updatedAt = t.updatedAt || t.createdAt
      if (updatedAt) {
        const ageMs = nowMs - (parseDbDate(updatedAt) ?? NaN)
        const ageDays = ageMs / (1000 * 60 * 60 * 24)
        if (ageDays < 7) score += 3
        else if (ageDays < 30) score += 2
        else if (ageDays < 90) score += 1
      }

      scored.push({ id: t.id as string, score, index: t.index })
    })

    // Sort by score descending, then by index as tiebreaker (a stable sort: ties keep scan order)
    scored.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return a.index - b.index
    })

    const rows = await db.totodo.bulkGet(scored.slice(0, limit).map((s) => s.id))
    return ok(rows.filter((r): r is DbObject => r !== undefined))
  } catch (e) {
    return fail(`searchTasks: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// Task detail — full info for a single task
// ---------------------------------------------------------------------------

export interface TaskDetail extends ChecklistView {
  id: string
  title: string
  note: string | null
  docHtml: string | null
  status: "active" | "completed" | "canceled"
  dueAt: string | null
  tags: string[]
  containerName: string | null
  containerId: string | null
  parentPath: string
  startAt: string | null
  startAtType: number | null
  /**
   * Number of live checklist rows (items AND headings) in the whole checklist —
   * always equals `checklistTotal`; `checklist` holds fewer when it is truncated.
   * Before the `checklist` field existed this counted every live object under the
   * task's checklist path; now it counts the same rows `checklist` pages through.
   */
  checklistCount: number
  reminder: { id: string; fireAt: string } | null
  createdAt: string
  updatedAt: string | undefined
}

export interface TaskDetailOptions {
  /** Which checklist rows to return (default: the first 200; progress always covers all). */
  checklist?: ChecklistViewPage
  /** Read the task's reminder (default true). When false, `reminder` is null. */
  includeReminder?: boolean
  /**
   * Whether the title of the task's container (project, collection, section, area) may be
   * shown (default: always). When it returns false, `containerName` is null; `containerId`
   * and `parentPath` stay. The server passes the grant's container scopes here.
   */
  includeContainerName?: (container: DbObject) => boolean
}

/**
 * Get full detail for a single task by ID.
 * Resolves container name, schedule info, checklist rows (+ progress), and reminder.
 */
export async function getTaskDetail(
  taskId: string,
  opts: TaskDetailOptions = {},
): Promise<OpResult<TaskDetail>> {
  try {
    const db = getDb()
    const task = await db.totodo.get(taskId)
    if (!task) return fail(`Task not found: ${taskId}`)

    // Resolve container name
    let containerName: string | null = null
    const pathParts = task.parentPath.split("/")
    const containerId = pathParts[pathParts.length - 1]
    if (containerId && !containerId.startsWith("#")) {
      const container = await db.totodo.get(containerId)
      if (container && (opts.includeContainerName?.(container) ?? true)) {
        containerName = container.title
      }
    }

    // Checklist rows — the same query the app's task detail uses
    const view = await getChecklistView(taskId, task.parentPath, opts.checklist)

    // Schedule from startAtIndex
    const startAtEntry = await db.startAtIndex.get(`#${taskId}`)

    // Reminder (the server leaves it out for a grant without reminders:read)
    const reminder = opts.includeReminder === false
      ? undefined
      : await db.reminder
        .where("[taskId+isDeleted]")
        .equals([taskId, 0])
        .first()

    return ok({
      id: task.id as string,
      title: task.title,
      note: task.note ?? null,
      docHtml: task.docHtml ?? null,
      status:
        task.status === ObjectStatus.Completed ? "completed" :
        task.status === ObjectStatus.Canceled ? "canceled" : "active",
      dueAt: task.dueAt ?? null,
      tags: task.tags ?? [],
      containerName,
      containerId: containerId && !containerId.startsWith("#") ? containerId : null,
      parentPath: task.parentPath,
      startAt: startAtEntry?.startAt ?? null,
      startAtType: startAtEntry?.type ?? null,
      checklistCount: view.checklistTotal,
      ...view,
      reminder: reminder ? { id: reminder.id as string, fireAt: reminder.fireAt ?? "" } : null,
      createdAt: task.createdAt,
      updatedAt: task.updatedAt,
    })
  } catch (e) {
    return fail(`getTaskDetail: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// List tags — aggregate tags from all active tasks
// ---------------------------------------------------------------------------

export interface TagInfo {
  name: string
  count: number
}

/** A live task or (legacy) subtask — what a tag "counts" and what a tag filter returns. */
function isLiveTaskLike(item: DbObject): boolean {
  return item.isDeleted !== 1 && (item.type === ObjectType.Task || item.type === ObjectType.Subtask)
}

/**
 * Visit every object that carries at least one tag, once each, never the rest of the table: the
 * keys come from the `*tags` multiEntry index (key-only), the objects are then read in chunks, so
 * no more than a chunk of them is in memory at a time.
 */
export async function eachTaggedObject(visit: (item: DbObject) => void): Promise<void> {
  const db = getDb()
  const ids = new Set<string>()
  await db.totodo.orderBy("tags").eachPrimaryKey((id) => {
    ids.add(id)
  })
  const all = [...ids]
  for (let i = 0; i < all.length; i += TAGGED_CHUNK) {
    for (const item of await db.totodo.bulkGet(all.slice(i, i + TAGGED_CHUNK))) {
      if (item) visit(item)
    }
  }
}

const TAGGED_CHUNK = 200

/**
 * Get all unique tags with their usage counts (live tasks and subtasks only —
 * the same set `listTasksByTag` returns, so a count always matches its list).
 *
 * Reads only the objects that carry a tag (see eachTaggedObject), once each —
 * not one query per tag, and never a scan of the whole table.
 */
export async function listAllTags(): Promise<OpResult<TagInfo[]>> {
  try {
    const counts = new Map<string, number>()
    await eachTaggedObject((item) => {
      if (!isLiveTaskLike(item)) return
      // `tags` comes from sync: a string here would be counted character by character
      if (!Array.isArray(item.tags)) return
      for (const name of new Set(item.tags)) counts.set(name, (counts.get(name) ?? 0) + 1)
    })

    const tags: TagInfo[] = [...counts].map(([name, count]) => ({ name, count }))
    tags.sort((a, b) => a.name.localeCompare(b.name))
    return ok(tags)
  } catch (e) {
    return fail(`listAllTags: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * List live tasks (and subtasks) that carry `tag`, via the `*tags` index.
 */
/**
 * One page of the live tasks carrying `tag`, with the real total (listTasksByTag's limit caps what
 * it reads, so its length is not a count). For callers that page through the matches.
 */
export async function listTasksByTagPage(
  tag: string,
  opts: { limit: number; offset: number },
): Promise<OpResult<{ items: DbObject[]; total: number }>> {
  try {
    const all = await getDb().totodo.where("tags").equals(tag).filter(isLiveTaskLike).toArray()
    return ok({ items: all.slice(opts.offset, opts.offset + opts.limit), total: all.length })
  } catch (e) {
    return fail(`listTasksByTagPage: ${e instanceof Error ? e.message : String(e)}`)
  }
}

export async function listTasksByTag(
  tag: string,
  limit = 50,
): Promise<OpResult<DbObject[]>> {
  try {
    const db = getDb()
    const items = await db.totodo
      .where("tags")
      .equals(tag)
      .filter(isLiveTaskLike)
      .limit(limit)
      .toArray()
    return ok(items)
  } catch (e) {
    return fail(`listTasksByTag: ${e instanceof Error ? e.message : String(e)}`)
  }
}
